/**
 * Static MarketHeader for SSR – no hooks, no JavaScript.
 * Renders the same hero + stats when a location filter is set. "Remove location" is a link.
 */
import React from 'react';
import { Wallet, Laptop, Languages } from 'lucide-react';
import { Link } from './Link';
import { getLocationBackgroundImage } from '../utils/location/get-location-background-image-url';
import type { LocationTrendsData } from '../hooks/useLocationTrends';
import type { MarketHeaderStaticData } from '../features/srp/SrpViewModel';
import type { SrpStaticStrings } from '../features/srp/SrpViewModel';

/** Literal Tailwind class strings for market header (no dynamic concatenation). */
const MARKET_HEADER_CARD_WITH_BG = 'bg-white/90 dark:bg-gray-900/90 border border-white/40 dark:border-gray-700/50';
const MARKET_HEADER_CARD_NO_BG = 'bg-white/80 dark:bg-gray-800/80 shadow-lg border border-gray-200/50 dark:border-gray-700/50';
const MARKET_HEADER_TITLE_WITH_BG = 'text-white';
const MARKET_HEADER_TITLE_NO_BG = 'text-gray-900 dark:text-white';
const MARKET_HEADER_SUBTEXT_WITH_BG = 'text-white/80';
const MARKET_HEADER_SUBTEXT_NO_BG = 'text-gray-600 dark:text-gray-400';
const MARKET_HEADER_OVERVIEW_WITH_BG = 'text-white/90';
const MARKET_HEADER_OVERVIEW_NO_BG = 'text-gray-700 dark:text-gray-300';

function workModelHeadline(
  onsite: number,
  hybrid: number,
  remote: number
): { headline: string; onsitePercent: number; hybridPercent: number; remotePercent: number } | null {
  const total = onsite + hybrid + remote;
  if (total === 0) return null;
  const onsitePercent = (onsite / total) * 100;
  const hybridPercent = (hybrid / total) * 100;
  const remotePercent = (remote / total) * 100;
  let headline: string;
  if (onsitePercent > 60) headline = 'Primarily Onsite';
  else if (hybridPercent > 40 || (hybridPercent > onsitePercent && hybridPercent > remotePercent)) headline = 'Hybrid Popular';
  else if (remotePercent > 30 || (remotePercent > onsitePercent && remotePercent > hybridPercent)) headline = 'Remote-Friendly';
  else if (hybridPercent + remotePercent > 60) headline = 'Flexible Work';
  else if (onsitePercent > hybridPercent && onsitePercent > remotePercent) headline = 'Primarily Onsite';
  else if (hybridPercent > remotePercent) headline = 'Hybrid Popular';
  else headline = 'Remote-Friendly';
  return {
    headline,
    onsitePercent: Math.round(onsitePercent),
    hybridPercent: Math.round(hybridPercent),
    remotePercent: Math.round(remotePercent),
  };
}

function WorkModelBarStatic({
  onsite,
  hybrid,
  remote,
}: {
  onsite: number;
  hybrid: number;
  remote: number;
}): React.ReactElement | null {
  const total = onsite + hybrid + remote;
  if (total === 0) return null;
  const onsitePercent = (onsite / total) * 100;
  const hybridPercent = (hybrid / total) * 100;
  const remotePercent = (remote / total) * 100;
  return (
    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex" aria-hidden>
      {onsitePercent > 0 && (
        <div className="bg-blue-600 dark:bg-blue-500" style={{ width: `${onsitePercent}%` }} />
      )}
      {hybridPercent > 0 && (
        <div className="bg-blue-400 dark:bg-blue-400" style={{ width: `${hybridPercent}%` }} />
      )}
      {remotePercent > 0 && (
        <div className="bg-blue-300 dark:bg-blue-300" style={{ width: `${remotePercent}%` }} />
      )}
    </div>
  );
}

function LanguageBarStatic({
  english,
  local,
}: {
  english: number;
  local: number;
}): React.ReactElement | null {
  const total = english + local;
  if (total === 0) return null;
  const englishPercent = (english / total) * 100;
  const localPercent = (local / total) * 100;
  return (
    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex" aria-hidden>
      {englishPercent > 0 && (
        <div className="bg-purple-600 dark:bg-purple-500" style={{ width: `${englishPercent}%` }} />
      )}
      {localPercent > 0 && (
        <div className="bg-purple-400 dark:bg-purple-400" style={{ width: `${localPercent}%` }} />
      )}
    </div>
  );
}

export interface MarketHeaderStaticProps {
  location: string;
  nbHits: number;
  locationTrendsData?: LocationTrendsData | null;
  marketHeaderStatic: MarketHeaderStaticData;
  strings: NonNullable<SrpStaticStrings['marketHeader']>;
}

export function MarketHeaderStatic({
  location,
  nbHits,
  locationTrendsData,
  marketHeaderStatic,
  strings,
}: MarketHeaderStaticProps): React.ReactElement {
  const bgImage = getLocationBackgroundImage(location);
  const isShowingBackgroundImage = Boolean(bgImage);

  const salaryStats = locationTrendsData?.salaryStats?.aggregate;
  const remotenessDistribution = locationTrendsData?.trends?.remotenessDistribution ?? {};
  const languageRequirements = locationTrendsData?.trends?.languageRequirements ?? {};
  const onsite = remotenessDistribution.onsite ?? 0;
  const hybrid = remotenessDistribution.hybrid ?? 0;
  const remote = remotenessDistribution.remote ?? 0;
  const workModel = workModelHeadline(onsite, hybrid, remote);
  const english = languageRequirements['English'] ?? 0;
  const finnish = languageRequirements['Finnish'] ?? languageRequirements['Suomi'] ?? 0;
  const swedish = languageRequirements['Swedish'] ?? languageRequirements['Svenska'] ?? 0;
  const local = finnish + swedish;
  const languageTotal = english + local;

  const salaryValue = salaryStats
    ? (() => {
        const avg = salaryStats.average?.min ?? salaryStats.median?.min ?? 0;
        return avg > 0 ? `€${Math.round(avg / 1000)}k/mo` : null;
      })()
    : null;
  const salaryRange = salaryStats
    ? (() => {
        const min = salaryStats.min ?? 0;
        const max = salaryStats.max ?? 0;
        const p25 = salaryStats.percentile25?.min ?? 0;
        const p75 = salaryStats.percentile75?.min ?? 0;
        if (min === 0 && max === 0) return null;
        if (p25 > 0 && p75 > 0) {
          return { low: p25, high: p75, isPercentile: true };
        }
        return { low: min, high: max, isPercentile: false };
      })()
    : null;

  const cardClass = isShowingBackgroundImage ? MARKET_HEADER_CARD_WITH_BG : MARKET_HEADER_CARD_NO_BG;
  const textClass = isShowingBackgroundImage ? MARKET_HEADER_TITLE_WITH_BG : MARKET_HEADER_TITLE_NO_BG;
  const subtextClass = isShowingBackgroundImage ? MARKET_HEADER_SUBTEXT_WITH_BG : MARKET_HEADER_SUBTEXT_NO_BG;

  return (
    <section
      id="location-info-header"
      className="location-info-header market-header relative overflow-hidden"
      aria-labelledby="location-header-title"
    >
      {isShowingBackgroundImage && bgImage ? (
        <div className="absolute inset-0 rounded-b-2xl sm:rounded-b-3xl overflow-hidden">
          <img
            src={bgImage.src}
            srcSet={bgImage.srcset || undefined}
            sizes="100vw"
            alt=""
            className="w-full h-full object-cover"
            aria-hidden
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20 rounded-b-2xl sm:rounded-b-3xl" />
      )}

      <div className="relative z-10 p-6 sm:p-8 lg:p-12">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <h1
              id="location-header-title"
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight ${textClass}`}
            >
              {marketHeaderStatic.title}
            </h1>
            <p className={`text-sm sm:text-base mt-2 ${subtextClass}`}>
              {nbHits.toLocaleString()} {nbHits === 1 ? 'job' : 'jobs'}
            </p>
          </div>
          <Link
            href={marketHeaderStatic.removeLocationUrl}
            className={`flex-shrink-0 px-3 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${isShowingBackgroundImage ? 'bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700' : 'bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'}`}
            aria-label={strings.removeLocation}
          >
            {strings.removeLocation}
          </Link>
        </div>

        {marketHeaderStatic.overviewText ? (
          <div className={`text-base sm:text-lg leading-relaxed max-w-5xl lg:max-w-6xl mb-6 ${isShowingBackgroundImage ? MARKET_HEADER_OVERVIEW_WITH_BG : MARKET_HEADER_OVERVIEW_NO_BG}`}>
            <p>{marketHeaderStatic.overviewText}</p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`rounded-[2.5rem] p-6 ${cardClass}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Wallet size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">{strings.salaryLabel}</div>
                {salaryValue && (
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{salaryValue}</div>
                )}
              </div>
            </div>
            {salaryRange && (
              <div className="mt-4">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  {salaryRange.isPercentile ? strings.salaryRangePercentile : strings.salaryRange}
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  €{Math.round(salaryRange.low / 1000)}k - €{Math.round(salaryRange.high / 1000)}k/mo
                </div>
              </div>
            )}
            {marketHeaderStatic.salaryBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {marketHeaderStatic.salaryBlurb}
              </p>
            )}
          </div>

          <div className={`rounded-[2.5rem] p-6 ${cardClass}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                <Laptop size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">{strings.workModelLabel}</div>
                {workModel ? (
                  <>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{workModel.headline}</div>
                    <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                      {strings.workModelOnsite.replace('{percent}', String(workModel.onsitePercent))} •{' '}
                      {strings.workModelHybrid.replace('{percent}', String(workModel.hybridPercent))} •{' '}
                      {strings.workModelRemote.replace('{percent}', String(workModel.remotePercent))}
                    </div>
                  </>
                ) : (
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{strings.workModelNoData}</div>
                )}
              </div>
            </div>
            {onsite + hybrid + remote > 0 && (
              <figure className="mt-4">
                <WorkModelBarStatic onsite={onsite} hybrid={hybrid} remote={remote} />
              </figure>
            )}
            {marketHeaderStatic.workModelBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {marketHeaderStatic.workModelBlurb}
              </p>
            )}
          </div>

          <div className={`rounded-[2.5rem] p-6 ${cardClass}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <Languages size={20} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">{strings.languageLabel}</div>
                {languageTotal > 0 ? (
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {Math.round((english / languageTotal) * 100)}% English
                  </div>
                ) : (
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">Mixed</div>
                )}
              </div>
            </div>
            {languageTotal > 0 && (
              <figure className="mt-4">
                <LanguageBarStatic english={english} local={local} />
              </figure>
            )}
            {marketHeaderStatic.languageBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {marketHeaderStatic.languageBlurb}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
